import { useMemo, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Box, CardActionArea, Divider, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { companiesAction, fundsAction } from 'common/actions';
import { firmHomeUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { Firm, FIRM_LIST_STATUSES } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { Input, LoadingState, InputAdornment as UiInputAdornment } from 'components/ui';
import FirmIconChip from './FirmIconChip';
import {
  FEATURE_CARDS,
  HOME_COMPANY_SEARCH_PLACEHOLDER,
  HOME_LOADING_COMPANIES,
  HOME_NO_COMPANIES_FOUND,
  HOME_NO_COMPANIES_MATCH_SEARCH,
  HOME_PORTFOLIO_COMPANIES_LABEL,
} from './Home.constants';
import type { FirmUrlBuilder } from './Home.props';
import {
  CompanyCell,
  CompanyGrid,
  CompanyHeading,
  CompanyListRoot,
  FeatureCard,
  FeatureGrid,
  FirmRibbon,
  HomeRoot,
  LetterGroup,
  LetterHeaderBox,
} from './Home.styles';
import { buildGridEntries, groupAlphabetically } from './Home.utils';

const HomePage = () => {
  const { firmSlugParam } = useParams<{ firmSlugParam: string }>();
  const navigate = useNavigate();
  const [{ firmList, firmListStatus, firmInfo, companyList, user }, dispatch] = useStore() as UseStoreValues;
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isStaff = Boolean(user?.is_staff);

  const resolvedFirmList = useMemo<Firm[]>(
    () => (firmListStatus === FIRM_LIST_STATUSES.READY && Array.isArray(firmList) ? (firmList as Firm[]) : []),
    [firmList, firmListStatus]
  );

  const isCompanyListLoading = companyList === null;
  const letterGroups = useMemo(
    () => groupAlphabetically(buildGridEntries((companyList ?? []) as FirmCompany[], query)),
    [companyList, query]
  );

  const handleFirmClick = (slug: string) => {
    if (slug !== firmSlugParam) {
      dispatch(companiesAction.setCompanyList(null));
      dispatch(fundsAction.setFundList(null));
    }
    navigate(firmHomeUrl(slug));
  };

  const handleFirmPageClick = (getUrl: FirmUrlBuilder) => {
    if (firmSlugParam) {
      navigate(getUrl(firmSlugParam));
    }
  };

  const renderCompanyGrid = () => {
    if (isCompanyListLoading) {
      return <LoadingState label={HOME_LOADING_COMPANIES} />;
    }

    if (letterGroups.length === 0) {
      return (
        <Typography color="text.secondary" variant="body2">
          {query ? HOME_NO_COMPANIES_MATCH_SEARCH : HOME_NO_COMPANIES_FOUND}
        </Typography>
      );
    }

    return (
      <CompanyListRoot>
        {letterGroups.map(({ char, entries }) => (
          <LetterGroup key={char}>
            <LetterHeaderBox>
              <Typography
                variant="caption"
                sx={theme => ({
                  color: theme.palette.text.secondary,
                  display: 'block',
                  fontWeight: 500,
                  mb: 0.75,
                })}>
                {char}
              </Typography>
              <Divider />
            </LetterHeaderBox>

            <CompanyGrid>
              {entries.map(entry => (
                <CompanyCell key={entry.key} onClick={() => handleFirmPageClick(entry.getUrl)}>
                  <Typography className="company-name" variant="body2">
                    {entry.label}
                  </Typography>
                </CompanyCell>
              ))}
            </CompanyGrid>
          </LetterGroup>
        ))}
      </CompanyListRoot>
    );
  };

  return (
    <HomeRoot>
      {/* Firm ribbon - is_staff only */}
      {isStaff && resolvedFirmList.length > 0 && (
        <FirmRibbon>
          {resolvedFirmList.map(firm => (
            <FirmIconChip key={firm.id} firm={firm} onClick={handleFirmClick} />
          ))}
        </FirmRibbon>
      )}

      {/* Feature cards */}
      <FeatureGrid>
        {FEATURE_CARDS.map(card => (
          <FeatureCard key={card.key} elevation={0} $bgColor={card.color}>
            <CardActionArea
              onClick={() => handleFirmPageClick(card.getUrl)}
              sx={{ height: '100%', p: 2.5 }}>
              <Box sx={{ color: card.iconColor, mb: 1.5 }}>{card.icon}</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {card.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {card.description}
              </Typography>
            </CardActionArea>
          </FeatureCard>
        ))}
      </FeatureGrid>

      {/* Search */}
      <Input
        autoFocus
        inputRef={inputRef}
        placeholder={HOME_COMPANY_SEARCH_PLACEHOLDER}
        value={query}
        onChange={e => setQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <UiInputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </UiInputAdornment>
            ),
          },
        }}
        sx={{ mb: 4 }}
      />

      {/* Company grid heading */}
      {firmInfo?.name && (
        <CompanyHeading>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {firmInfo.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {HOME_PORTFOLIO_COMPANIES_LABEL}
          </Typography>
        </CompanyHeading>
      )}

      {/* Alphabetical company grid */}
      {renderCompanyGrid()}
    </HomeRoot>
  );
};

export default HomePage;
